import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PinVerificationModal from "../components/PinVerificationModal";

export default function AgentDeposit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  // Customer search
  const [searchType, setSearchType] = useState("email");
  const [searchValue, setSearchValue] = useState("");
  const [customer, setCustomer] = useState(null);

  // Deposit form
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  // Agent info
  const [agentBalance, setAgentBalance] = useState(0);

  // PIN Verification Modal
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    loadAgentBalance();
    checkTwoFactorStatus();
  }, []);

  const loadAgentBalance = async () => {
    try {
      const res = await api.get("/Wallet/balance");
      setAgentBalance(res.data.balance);
    } catch (err) {
      console.error("Failed to load balance");
    }
  };

  const checkTwoFactorStatus = async () => {
    try {
      const res = await api.get("/TwoFactor/status");
      setTwoFactorEnabled(res.data.isEnabled);
    } catch (err) {
      // 2FA might not be set up yet
      setTwoFactorEnabled(false);
    }
  };

  const searchCustomer = async () => {
    if (!searchValue) {
      setError("Please enter a search value");
      return;
    }

    setLoading(true);
    setError("");
    setCustomer(null);

    try {
      // Use the correct endpoint: /api/Wallet/agent/search-customer
      const queryParam = searchType === "email"
        ? `email=${encodeURIComponent(searchValue)}`
        : `accountNumber=${searchValue}`;

      const res = await api.get(`/Wallet/agent/search-customer?${queryParam}`);
      setCustomer(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Customer not found");
    } finally {
      setLoading(false);
    }
  };

  // Step 1: User clicks "Deposit" button - validate and check if PIN is needed
  const handleDepositClick = () => {
    if (!customer) {
      setError("Please find a customer first");
      return;
    }

    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setError("");

    // Store the pending deposit details - use email or accountNumber based on search type
    const depositData = {
      customerEmail: searchType === "email" ? searchValue : null,
      customerAccountNumber: searchType === "account" ? searchValue : customer.accountNumber,
      amount: Number(amount),
      description: description || "Cash deposit"
    };

    setPendingDeposit(depositData);

    // Check if 2FA is enabled - if yes, show PIN modal
    if (twoFactorEnabled) {
      setShowPinModal(true);
    } else {
      // No 2FA, proceed directly
      executeDeposit(depositData);
    }
  };

  // Step 2: After PIN verification (or if no PIN required), execute the deposit
  const handlePinVerified = () => {
    if (pendingDeposit) {
      executeDeposit(pendingDeposit);
    }
  };

  // Step 3: Actually call the API to process the deposit
  const executeDeposit = async (depositData) => {
    setLoading(true);
    setError("");

    try {
      const res = await api.post("/Wallet/agent/deposit", depositData);

      setSuccess({
        customer: res.data.customerName,
        account: res.data.customerAccountNumber,
        amount: res.data.grossAmount,
        fee: res.data.feeAmount || 0,
        credited: res.data.netAmount,
        customerBalance: res.data.customerNewBalance,
        agentBalance: res.data.agentFloatBalance,
        reference: res.data.journalNumber,
        companyShare: res.data.companyShare,
        agentShare: res.data.agentShare
      });

      // Update agent balance
      setAgentBalance(res.data.agentFloatBalance);

      // Reset form
      setCustomer(null);
      setSearchValue("");
      setAmount("");
      setDescription("");
      setPendingDeposit(null);
    } catch (err) {
      setError(err.response?.data?.message || "Deposit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewDeposit = () => {
    setSuccess(null);
    setCustomer(null);
    setSearchValue("");
    setAmount("");
    setDescription("");
  };

  const formatCurrency = (amount) => `₦${Number(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>💰 Agent Deposit</h1>
        <div style={styles.headerRight}>
          <div style={styles.balanceCard}>
            <span style={styles.balanceLabel}>Your Float:</span>
            <span style={styles.balanceValue}>{formatCurrency(agentBalance)}</span>
          </div>
          <button onClick={() => navigate("/dashboard")} style={styles.btnOutline}>
            ← Dashboard
          </button>
        </div>
      </div>

      <p style={styles.subtitle}>Accept cash from customer and credit their wallet</p>

      {error && <div style={styles.error}>{error}</div>}

      {/* Success Modal */}
      {success && (
        <div style={styles.successModal}>
          <div style={styles.successContent}>
            <div style={styles.successHeader}>✓ Deposit Successful</div>
            <div style={styles.successBody}>
              <div style={styles.successRow}>
                <span>Customer</span>
                <strong>{success.customer}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Account</span>
                <strong>{success.account}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Amount</span>
                <strong>{formatCurrency(success.amount)}</strong>
              </div>
              {success.fee > 0 && (
                <div style={styles.successRow}>
                  <span>Fee</span>
                  <strong style={{ color: "#dc3545" }}>-{formatCurrency(success.fee)}</strong>
                </div>
              )}
              <div style={styles.successRow}>
                <span>Credited</span>
                <strong style={{ color: "#28a745" }}>{formatCurrency(success.credited)}</strong>
              </div>
              <div style={styles.divider}></div>
              <div style={styles.successRow}>
                <span>Cust. Balance</span>
                <strong>{formatCurrency(success.customerBalance)}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Your Float</span>
                <strong>{formatCurrency(success.agentBalance)}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Ref</span>
                <code style={styles.refCode}>{success.reference}</code>
              </div>
            </div>
            <div style={styles.successFooter}>
              <button onClick={handleNewDeposit} style={styles.btnOutline}>New</button>
              <button onClick={() => navigate("/dashboard")} style={styles.btnPrimary}>Done</button>
            </div>
          </div>
        </div>
      )}

      {!success && (
        <>
          {/* Step 1: Find Customer */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>1. Find Customer</h3>
            <div style={styles.searchTypeButtons}>
              <button
                onClick={() => setSearchType("email")}
                style={{
                  ...styles.searchTypeBtn,
                  ...(searchType === "email" ? styles.searchTypeBtnActive : {})
                }}
              >
                By Email
              </button>
              <button
                onClick={() => setSearchType("account")}
                style={{
                  ...styles.searchTypeBtn,
                  ...(searchType === "account" ? styles.searchTypeBtnActive : {})
                }}
              >
                By Account Number
              </button>
            </div>
            <div style={styles.searchRow}>
              <input
                type={searchType === "email" ? "email" : "text"}
                placeholder={searchType === "email" ? "Enter customer email" : "Enter account number"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={styles.input}
                onKeyDown={(e) => e.key === "Enter" && searchCustomer()}
              />
              <button 
                onClick={searchCustomer} 
                disabled={loading}
                style={styles.btnPrimary}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>

            {customer && (
              <div style={styles.customerCard}>
                <div style={styles.customerAvatar}>
                  {customer.fullName?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div style={styles.customerInfo}>
                  <div style={styles.customerName}>{customer.fullName}</div>
                  <div style={styles.customerAccount}>{customer.accountNumber}</div>
                  <div style={styles.customerBalance}>
                    Balance: {formatCurrency(customer.balance)}
                  </div>
                </div>
                <div style={styles.customerCheck}>✓</div>
              </div>
            )}
          </div>

          {/* Step 2: Enter Amount */}
          {customer && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>2. Enter Deposit Amount</h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (₦)</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={styles.amountInput}
                  min="1"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g., Cash deposit"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={styles.input}
                />
              </div>

              {/* 2FA Notice */}
              {twoFactorEnabled ? (
                <div style={styles.pinNotice}>
                  🔐 You will be asked to enter your transaction PIN to authorize this deposit
                </div>
              ) : (
                <div style={styles.pinWarning}>
                  ⚠️ <a href="/pin-setup" style={styles.link}>Set up a transaction PIN</a> for added security
                </div>
              )}

              <button
                onClick={handleDepositClick}
                disabled={loading || !amount}
                style={{
                  ...styles.btnDeposit,
                  opacity: loading || !amount ? 0.6 : 1
                }}
              >
                {loading ? "Processing..." : `Deposit ${formatCurrency(amount || 0)}`}
              </button>
            </div>
          )}
        </>
      )}

      {/* PIN Verification Modal */}
      <PinVerificationModal
        isOpen={showPinModal}
        onClose={() => {
          setShowPinModal(false);
          setPendingDeposit(null);
        }}
        onVerified={handlePinVerified}
        transactionType="AgentDeposit"
        amount={pendingDeposit?.amount}
        title="Authorize Deposit"
      />
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "600px",
    margin: "0 auto",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    flexWrap: "wrap",
    gap: "10px"
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },
  title: {
    margin: 0,
    fontSize: "24px"
  },
  subtitle: {
    color: "#666",
    marginBottom: "20px"
  },
  balanceCard: {
    backgroundColor: "#e3f2fd",
    padding: "8px 15px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  balanceLabel: {
    fontSize: "12px",
    color: "#1565c0"
  },
  balanceValue: {
    fontWeight: "bold",
    color: "#1565c0"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
  },
  cardTitle: {
    margin: "0 0 15px 0",
    fontSize: "16px",
    color: "#333"
  },
  searchTypeButtons: {
    display: "flex",
    gap: "10px",
    marginBottom: "15px"
  },
  searchTypeBtn: {
    flex: 1,
    padding: "10px",
    border: "1px solid #ddd",
    backgroundColor: "white",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s"
  },
  searchTypeBtnActive: {
    backgroundColor: "#007bff",
    color: "white",
    borderColor: "#007bff"
  },
  searchRow: {
    display: "flex",
    gap: "10px"
  },
  input: {
    flex: 1,
    padding: "12px 15px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px"
  },
  amountInput: {
    width: "100%",
    padding: "15px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    boxSizing: "border-box"
  },
  formGroup: {
    marginBottom: "15px"
  },
  label: {
    display: "block",
    marginBottom: "5px",
    fontWeight: "500",
    color: "#333"
  },
  customerCard: {
    display: "flex",
    alignItems: "center",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    marginTop: "15px",
    border: "1px solid #e9ecef"
  },
  customerAvatar: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor: "#007bff",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: "bold",
    marginRight: "15px"
  },
  customerInfo: {
    flex: 1
  },
  customerName: {
    fontWeight: "bold",
    fontSize: "16px"
  },
  customerAccount: {
    color: "#666",
    fontFamily: "monospace"
  },
  customerBalance: {
    color: "#28a745",
    fontSize: "14px",
    marginTop: "3px"
  },
  customerCheck: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    backgroundColor: "#28a745",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px"
  },
  pinNotice: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "13px"
  },
  pinWarning: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "13px"
  },
  link: {
    color: "#856404",
    fontWeight: "500"
  },
  btnPrimary: {
    padding: "12px 20px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  btnOutline: {
    padding: "10px 20px",
    backgroundColor: "white",
    color: "#007bff",
    border: "1px solid #007bff",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer"
  },
  btnDeposit: {
    width: "100%",
    padding: "15px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    fontWeight: "bold",
    cursor: "pointer"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px"
  },
  successModal: {
    backgroundColor: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
  },
  successContent: {},
  successHeader: {
    backgroundColor: "#28a745",
    color: "white",
    padding: "15px 20px",
    fontSize: "18px",
    fontWeight: "bold"
  },
  successBody: {
    padding: "20px"
  },
  successRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #f0f0f0"
  },
  divider: {
    height: "1px",
    backgroundColor: "#ddd",
    margin: "10px 0"
  },
  refCode: {
    backgroundColor: "#f8f9fa",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "12px"
  },
  successFooter: {
    display: "flex",
    gap: "10px",
    padding: "15px 20px",
    backgroundColor: "#f8f9fa"
  }
};

